import { Avatar, AvatarGroup, Box, Button, Chip, Divider, IconButton, Menu, MenuItem, Tab, Tabs, Typography } from "@mui/material"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { useContext, useEffect, useMemo, useState } from "react"
import { UserContext } from "../../context/UserContext"
import { useNavigate, useParams } from "react-router"
import { useQuery } from "@tanstack/react-query"
import { CalendarMonth, EditOutlined, LocationOn, MoreVert, PeopleAlt } from "@mui/icons-material"
import MembersPopUpList from "../../components/members_popup.list"
import { getEvent } from "../../controllers/event/event"
import { CategoryDisplay } from "../../components/category.display"
import EventMixer from "../../components/event/EventMixer"
import { RSVPEventButton } from "../../components/event/rsvp_event.button"
import { MediaDisplayGallery } from "../../components/media_display.gallery.jsx"
import { getAccount } from "../../controllers/accounts/accounts.js"
import { extractLearnMoreLinks } from "../../util/util.js"
import CheckInModal from "../../components/event/CheckInModal.jsx"
import { getCheckins } from "../../controllers/checkin/checkin.js"

const SectionLabel = ({ label, color = '#732982' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ width: 4, height: 18, borderRadius: 2, background: color, flexShrink: 0 }} />
        <Typography sx={{ fontWeight: 800, fontSize: 14, color: '#23232b', letterSpacing: 0.2 }}>{label}</Typography>
    </Box>
)

export const ShowEvent = () => {
    const [leaders, setLeaders] = useState([])
    const [members, setMembers] = useState([])
    const [activeTab, setActiveTab] = useState('details')
    const { id } = useParams()
    const { userDetails } = useContext(UserContext)
    const token = userDetails?.token
    const { data: event } = useQuery({ queryKey: ['getSingleEvent', id, token], queryFn: () => getEvent(token, id) })

    const single_event = useMemo(() => event?.event ?? {}, [event?.event])
    const about = useMemo(() => extractLearnMoreLinks(event?.content), [event?.content])

    // "Check In" only replaces the Pre-Mixer tab while the event is actually
    // in progress — an event whose end_date_time has already passed should
    // fall back to the (read-only, past) Pre-Mixer view, not show Check In forever.
    const now = new Date()
    const hasStarted = single_event?.start_date_time
        ? now >= new Date(single_event.start_date_time) && (!single_event?.end_date_time || now <= new Date(single_event.end_date_time))
        : false
    const [checkInOpen, setCheckInOpen] = useState(false)
    const { data: checkinsData, refetch: refetchCheckins } = useQuery({
        queryKey: ['getCheckins', single_event?._id, token],
        queryFn: () => getCheckins(token, single_event?._id),
        enabled: hasStarted && !!token && !!single_event?._id,
    })
    const isCheckedIn = (checkinsData?.checked_in ?? []).some(c => c.user === userDetails?.id)

    useEffect(() => {
        const loadParticipants = async () => {
            if (!token) return
            const uniqueAuthors = [...new Set(single_event?.author || [])]
            const uniqueRsvps = [...new Set(single_event?.RSVP || [])]
            try {
                const [leaderAccounts, memberAccounts] = await Promise.all([
                    Promise.all(uniqueAuthors.map(mid => getAccount(mid, token))),
                    Promise.all(uniqueRsvps.map(mid => getAccount(mid, token))),
                ])
                setLeaders(leaderAccounts.filter(Boolean))
                setMembers(memberAccounts.filter(Boolean))
            } catch {
                setLeaders([])
                setMembers([])
            }
        }
        loadParticipants()
    }, [single_event?.author, single_event?.RSVP, token])

    const navigate = useNavigate()
    const [anchorEl, setAnchorEl] = useState(null)
    const openMenu = Boolean(anchorEl)
    const [membersOpen, setMembersOpen] = useState(false)

    const canRSVP = !single_event?.RSVP?.includes(userDetails?.id) && !single_event?.author?.includes(userDetails?.id)
    const isAuthor = single_event?.author?.includes(userDetails?.id)
    const isAttending = single_event?.RSVP?.includes(userDetails?.id)
    const hasLocationAddress = Boolean(single_event?.location?.formatted_address)
    const lat = single_event?.location?.geometry?.location?.lat
    const lng = single_event?.location?.geometry?.location?.lng
    const hasCoords = typeof lat === 'number' && typeof lng === 'number'
    const clusterOverflow = Math.max(0, (leaders.length + members.length) - 5)

    return (
        <MainLoggedInLayout>
            <Box sx={{ maxWidth: 780, mx: 'auto', width: '100%' }}>

                {/* ── Hero image ─────────────────────────────────────── */}
                <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', mb: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
                    <img
                        src={single_event?.header_img}
                        alt={single_event?.title}
                        style={{ width: '100%', maxHeight: 380, objectFit: 'cover', display: 'block' }}
                    />
                    {/* gradient overlay */}
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)',
                    }} />

                    {/* Pride top bar */}
                    <Box sx={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: 5,
                        background: 'linear-gradient(90deg,#E40303,#FF8C00,#FFED00,#008026,#24408E,#732982)',
                    }} />

                    {/* Content overlay */}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 2, sm: 3 } }}>
                        <CategoryDisplay label={single_event?.category} hideLabel />
                        <Typography variant="h4" fontWeight={900} sx={{ color: '#fff', mt: 0.75, mb: 1, lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                            {single_event?.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Chip
                                icon={<PeopleAlt sx={{ fontSize: 14, color: '#fff !important' }} />}
                                label={`${single_event?.RSVP?.length || 0} attending`}
                                size="small"
                                sx={{ background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700, backdropFilter: 'blur(4px)' }}
                            />
                            {event?.replies_count > 0 && (
                                <Chip
                                    label={`${event.replies_count} comments`}
                                    size="small"
                                    sx={{ background: 'rgba(255,255,255,0.14)', color: '#fff', fontWeight: 700, backdropFilter: 'blur(4px)' }}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Actions top-right */}
                    <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 1 }}>
                        {isAuthor && (
                            <IconButton
                                size="small"
                                onClick={() => navigate('/event/edit/' + single_event?._id)}
                                sx={{ background: 'rgba(255,255,255,0.18)', color: '#fff', backdropFilter: 'blur(4px)', '&:hover': { background: 'rgba(255,255,255,0.32)' } }}
                            >
                                <EditOutlined fontSize="small" />
                            </IconButton>
                        )}
                        <IconButton
                            size="small"
                            onClick={e => setAnchorEl(e.currentTarget)}
                            sx={{ background: 'rgba(255,255,255,0.18)', color: '#fff', backdropFilter: 'blur(4px)', '&:hover': { background: 'rgba(255,255,255,0.32)' } }}
                        >
                            <MoreVert fontSize="small" />
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)}>
                            {isAuthor && (
                                <MenuItem onClick={() => { setAnchorEl(null); navigate('/event/edit/' + single_event?._id) }}>Edit event</MenuItem>
                            )}
                            {canRSVP && (
                                <MenuItem onClick={() => setAnchorEl(null)}>
                                    <RSVPEventButton event_details={single_event} />
                                </MenuItem>
                            )}
                        </Menu>
                    </Box>
                </Box>

                {/* ── RSVP banner ─────────────────────────────────────── */}
                {canRSVP && (
                    <Box sx={{
                        mb: 3, p: 2, borderRadius: 2.5,
                        background: 'linear-gradient(120deg,#f3f0ff,#fff0f6)',
                        border: '1px solid rgba(115,41,130,0.18)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
                    }}>
                        <Box>
                            <Typography fontWeight={700} fontSize={15}>Join this event</Typography>
                            <Typography variant="body2" color="text.secondary" fontSize={13}>RSVP to attend and join the group chat.</Typography>
                        </Box>
                        <RSVPEventButton event_details={single_event} />
                    </Box>
                )}
                {isAttending && (
                    <Box sx={{
                        mb: 3, p: 1.5, borderRadius: 2,
                        background: 'linear-gradient(90deg,rgba(0,128,38,0.08),rgba(36,64,142,0.08))',
                        border: '1px solid rgba(0,128,38,0.2)',
                        display: 'flex', alignItems: 'center', gap: 1,
                    }}>
                        <Typography fontSize={13} fontWeight={700} color="#008026">You're attending this event 🎉</Typography>
                    </Box>
                )}

                {/* ── Tabs ─────────────────────────────────────────────── */}
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{
                        mb: 3,
                        minHeight: 40,
                        "& .MuiTab-root": { textTransform: "none", fontWeight: 700, minHeight: 40 },
                        "& .MuiTabs-indicator": { background: "linear-gradient(90deg,#E40303,#732982)" },
                    }}
                >
                    <Tab label="Details" value="details" />
                    <Tab label={hasStarted ? "Check In" : "Pre-Mixer"} value="mixer" />
                    <Tab label="Location" value="location" />
                </Tabs>

                {/* ── Details tab ──────────────────────────────────────── */}
                {activeTab === 'details' && (
                    <>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 3 }}>
                            <Box sx={{
                                width: 40, height: 40, borderRadius: 2, flexShrink: 0,
                                background: 'linear-gradient(135deg,#E40303,#FF8C00)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <CalendarMonth sx={{ color: '#fff', fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography fontSize={11} fontWeight={700} color="#888" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>When</Typography>
                                <Typography fontSize={14} fontWeight={700}>
                                    {single_event?.start_date_time ? new Date(single_event.start_date_time).toLocaleDateString([], { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }) : '—'}
                                </Typography>
                                <Typography fontSize={12} color="text.secondary">
                                    {single_event?.start_date_time ? new Date(single_event.start_date_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                    {single_event?.end_date_time ? ` – ${new Date(single_event.end_date_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {leaders.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <SectionLabel label="Shared by" color="#732982" />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {leaders.map((leader, i) => (
                                        <Box
                                            key={i}
                                            onClick={() => navigate('/profile/' + leader.id)}
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                        >
                                            <Avatar src={leader.avatar} sx={{ width: 40, height: 40, border: '2px solid #732982' }} />
                                            <Box>
                                                <Typography fontSize={14} fontWeight={700}>{leader.display_name || leader.username}</Typography>
                                                <Typography fontSize={12} color="text.secondary">@{leader.username}</Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {event?.content && (
                            <Box sx={{ mb: 3 }}>
                                <SectionLabel label="About" color="#008026" />
                                <Typography component="div" sx={{ fontSize: 14, lineHeight: 1.7, color: '#3a3a4a' }}>
                                    <div dangerouslySetInnerHTML={{ __html: about.html }} />
                                </Typography>
                                {about.links.map((href, i) => (
                                    <Button
                                        key={i}
                                        variant="contained"
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ mt: 1.5, mr: 1, textTransform: 'none', fontWeight: 700, borderRadius: 2, background: '#008026', '&:hover': { background: '#006420' } }}
                                    >
                                        Learn More
                                    </Button>
                                ))}
                            </Box>
                        )}

                        {event?.media_attachments?.length > 0 && (
                            <>
                                <Divider sx={{ mb: 3 }} />
                                <Box sx={{ mb: 3 }}>
                                    <SectionLabel label="Gallery" color="#E40303" />
                                    <MediaDisplayGallery mediaList={event.media_attachments} />
                                </Box>
                            </>
                        )}
                    </>
                )}

                {/* ── Pre-Mixer / Check In tab ──────────────────────────── */}
                {activeTab === 'mixer' && (
                    <Box sx={{ mb: 2 }}>
                        {hasStarted ? (
                            <Box sx={{
                                textAlign: 'center', p: 4, borderRadius: 3,
                                background: 'linear-gradient(120deg,#f3f0ff,#fff0f6)',
                                border: '1px solid rgba(115,41,130,0.15)',
                            }}>
                                {isCheckedIn ? (
                                    <>
                                        <Typography sx={{ fontSize: 34, mb: 1 }}>✅</Typography>
                                        <Typography fontWeight={800} fontSize={16}>You're checked in</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Head over to Your Party to see who you've paired up with.
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography sx={{ fontSize: 34, mb: 1 }}>🎉</Typography>
                                        <Typography fontWeight={800} fontSize={16} sx={{ mb: 2 }}>This event has started!</Typography>
                                        <Button
                                            variant="contained"
                                            onClick={() => setCheckInOpen(true)}
                                            sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 700, background: '#732982', '&:hover': { background: '#5c1f68' } }}
                                        >
                                            Check In
                                        </Button>
                                        <CheckInModal
                                            eventId={single_event?._id}
                                            open={checkInOpen}
                                            setOpen={setCheckInOpen}
                                            onCheckedIn={refetchCheckins}
                                        />
                                    </>
                                )}
                            </Box>
                        ) : (
                            <EventMixer
                                id={event?.id}
                                token={token}
                                canPost={isAttending || isAuthor}
                            />
                        )}
                    </Box>
                )}

                {/* ── Location tab ─────────────────────────────────────── */}
                {activeTab === 'location' && (
                    <>
                        {single_event?.is_online ? (
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2.5 }}>
                                    <Box sx={{
                                        width: 40, height: 40, borderRadius: 2, flexShrink: 0,
                                        background: 'linear-gradient(135deg,#24408E,#732982)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <LocationOn sx={{ color: '#fff', fontSize: 20 }} />
                                    </Box>
                                    <Box>
                                        <Typography fontSize={11} fontWeight={700} color="#888" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Online Event</Typography>
                                        {single_event.meeting_link ? (
                                            <Typography fontSize={14} fontWeight={700}>{single_event.meeting_link}</Typography>
                                        ) : (
                                            <Typography fontSize={14} color="text.secondary">
                                                {hasStarted
                                                    ? "This party hasn't posted a meeting link. Check in to link up with who else is here."
                                                    : "No meeting link yet — once the party begins, check in to link up with who else is here."}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                                {single_event.meeting_link && (
                                    <Button
                                        variant="contained"
                                        href={single_event.meeting_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ mr: 1.5 }}
                                    >
                                        Join Meeting
                                    </Button>
                                )}
                                {hasStarted && (
                                    <Button
                                        variant={single_event.meeting_link ? "outlined" : "contained"}
                                        onClick={() => setActiveTab('mixer')}
                                    >
                                        {isCheckedIn ? "You're checked in" : 'Check In'}
                                    </Button>
                                )}
                            </Box>
                        ) : hasLocationAddress ? (
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2.5 }}>
                                    <Box sx={{
                                        width: 40, height: 40, borderRadius: 2, flexShrink: 0,
                                        background: 'linear-gradient(135deg,#24408E,#732982)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <LocationOn sx={{ color: '#fff', fontSize: 20 }} />
                                    </Box>
                                    <Box>
                                        <Typography fontSize={11} fontWeight={700} color="#888" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Where</Typography>
                                        <Typography fontSize={14} fontWeight={700}>{single_event.location.formatted_address}</Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden' }}>
                                    <iframe
                                        width="100%"
                                        height="320"
                                        style={{ border: 0, display: 'block' }}
                                        loading="lazy"
                                        title="Google Maps"
                                        allowFullScreen
                                        referrerPolicy="no-referrer-when-downgrade"
                                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBUjLE4mdiLnDtS0TDOPgLJYe27yeEjoRc&q=${encodeURIComponent(single_event.location.formatted_address)}`}
                                    />

                                    {/* Host + attending avatars clustered around the location marker */}
                                    {(leaders.length + members.length) > 0 && (
                                        <Box
                                            onClick={() => setMembersOpen(true)}
                                            sx={{
                                                position: 'absolute', left: 0, right: 0, top: 56,
                                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                                cursor: 'pointer', pointerEvents: 'auto',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {leaders.slice(0, 3).map((m, i) => (
                                                    <Avatar
                                                        key={'l' + i}
                                                        src={m?.avatar}
                                                        sx={{
                                                            width: 38, height: 38, ml: i === 0 ? 0 : -1.25,
                                                            border: '3px solid #F5B942', zIndex: 10 - i,
                                                        }}
                                                    />
                                                ))}
                                                {members.slice(0, 5 - Math.min(leaders.length, 3)).map((m, i) => (
                                                    <Avatar
                                                        key={'m' + i}
                                                        src={m?.avatar}
                                                        sx={{ width: 32, height: 32, ml: -1.25, border: '2px solid #fff', zIndex: 5 - i }}
                                                    />
                                                ))}
                                                {clusterOverflow > 0 && (
                                                    <Box sx={{
                                                        width: 32, height: 32, borderRadius: '50%', ml: 0.75,
                                                        background: '#202c41', border: '2px solid #fff',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 800 }}>+{clusterOverflow}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box sx={{ mt: 1, px: 1.5, py: 0.5, borderRadius: 2, background: 'rgba(0,0,0,0.72)' }}>
                                                <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 700 }} noWrap>
                                                    {leaders[0]?.display_name || leaders[0]?.username
                                                        ? `Shared by ${leaders[0]?.display_name || leaders[0]?.username}`
                                                        : 'Event location'}
                                                    {members.length > 0 ? ` · ${members.length} attending` : ''}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        ) : (
                            <Typography color="text.secondary" fontSize={14} sx={{ mb: 3 }}>No address provided.</Typography>
                        )}

                        {single_event?.RSVP?.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <SectionLabel label={`Attendees (${single_event.RSVP.length})`} color="#FF8C00" />
                                <MembersPopUpList
                                    leaders={leaders}
                                    community_id={single_event?._id}
                                    title="RSVP List"
                                    members={members}
                                    open={membersOpen}
                                    setOpen={setMembersOpen}
                                />
                                <AvatarGroup
                                    total={single_event.RSVP.length}
                                    onClick={() => setMembersOpen(true)}
                                    sx={{ justifyContent: 'flex-start', cursor: 'pointer', '& .MuiAvatar-root': { border: '2px solid #fff', width: 36, height: 36 } }}
                                >
                                    {members.map((m, i) => <Avatar key={i} src={m.avatar} />)}
                                </AvatarGroup>
                            </Box>
                        )}
                    </>
                )}

            </Box>
        </MainLoggedInLayout>
    )
}
